import { flow, find, map, result, reduce, each, endsWith, remove } from 'lodash';
import { curriedFilter, curriedSortBy, prop } from '@adjust/dashboard-core/lib/fp';

function columnSelection() {
  'ngInject';
  return {
    restrict: 'E',
    scope: {},
    template: require('./column-selection.html'),
    controller: 'ColumnSelectionController',
    controllerAs: 'vm',
    bindToController: {
      show: '=',
      ngModel: '=',
      screen: '@',
      events: '=',
      attributionType: '=',
      attributionSource: '=',
      loadingState: '=',
      onClose: '&',
      grouping: '=',
      trackerToken: '=',
    },
  };
}

function ColumnSelectionController(Column, Kpi, Helper, $scope, $filter, $q) {
  'ngInject';
  var vm = this;
  var _totals = {};

  vm.apply = apply;
  vm.selectAll = selectAll;
  vm.deselectAll = deselectAll;
  vm.select = select;
  vm.deselect = deselect;
  vm.handleSearchChange = _checkAllSelected;
  vm.clearSearch = clearSearch;
  vm.activateSearch = activateSearch;
  vm.updateOrder = updateOrder;
  vm.preventSubmit = preventSubmit;

  $scope.$watch('vm.loadingState', _handleLoadingChange);
  $scope.$watch('vm.show', _handleVisibility);
  $scope.$on('$destroy', _handleDestroy);

  /**
   * Handle loading state change callback
   *
   * @param {Boolean} loading loading state
   * @private
   */
  function _handleLoadingChange(loading) {
    vm.loading = loading;

    if (loading) {
      return;
    }

    _totals = Column.getTotals();

    _prepareColumns();
  }

  /**
   * Handle columns section visibility and prepare columns if shown
   *
   * @param {Boolean} n old state
   * @param {Boolean} o new state
   * @private
   */
  function _handleVisibility(n, o) {
    if (n !== o && n) {
      _prepareColumns();
    }
  }

  /**
   * Prepare columns list
   *
   * @private
   */
  function _prepareColumns() {
    var persistentColumns = Column.getPersistentColumns(vm.screen, vm.base);

    return $q
      .all([
        Kpi.getAll({
          attributionType: vm.attributionType,
          attributionSource: vm.attributionSource,
          grouping: vm.grouping,
          trackerToken: vm.trackerToken,
        }),
        Column.getColumns({
          split: true,
          attributionType: vm.attributionType,
          attributionSource: vm.attributionSource,
          tmpChange: true,
        }),
      ])
      .then(function(results) {
        var [kpis, columnsDetails] = results;
        var selectedColumns = columnsDetails.columns;
        var events = flow(
          curriedFilter(event => event.token !== event.name && (!event.isRevenue || (event.isRevenue && event.sum > 0))),
          curriedSortBy(prop('name'))
        )(vm.events);
        var oldConversionRate = find(vm.columns, _conversionFind);
        var newConversionRate;
        var finalList;

        kpis = map(kpis, function(kpi) {
          return { name: kpi, token: kpi, hide: result(find(vm.events, { token: kpi }), 'hide', false) };
        });
        finalList = kpis.concat(events);

        vm.columns = map(finalList, function(event) {
          var name = event.originalName || event.name;
          var label = Helper.translateKpi(name, vm.period, event.token);
          return {
            name: name,
            token: event.token,
            isCustom: event.isCustom,
            isRevenue: event.isRevenue,
            common: event.common,
            hide: event.hide,
            label: label,
            expandedLabel: _expandAbbreviatedLabel(label),
            selected:
              selectedColumns && selectedColumns.length
                ? selectedColumns.includes(event.token)
                : persistentColumns.includes(event.token) || !_isNull(event),
          };
        });

        newConversionRate = find(vm.columns, _conversionFind);

        if (oldConversionRate && newConversionRate) {
          newConversionRate.selected =
            oldConversionRate.selected &&
            (selectedColumns && selectedColumns.length
              ? selectedColumns.includes(newConversionRate.token)
              : !_isNull(newConversionRate));
        }

        vm.selectedColumns = _filterSelected(columnsDetails.order);

        clearSearch();
      });
  }

  /**
   * Check if particular event has no data
   *
   * @param {Object} event
   * @returns {Boolean}
   * @private
   */
  function _isNull(event) {
    return _totals[event.token] ? _totals[event.token].sum === 0 : true;
  }

  /**
   * Get the conversion rate column
   *
   * @param {Object} column
   * @returns {Boolean}
   * @private
   */
  function _conversionFind(column) {
    return column.token.includes('_conversion_rate');
  }

  /**
   * Get selected columns
   *
   * @returns {Array} list of selected columns
   * @private
   */
  function _filterSelected(order) {
    const selected = Column.filterSelected(vm.columns, 'isRevenue');

    return Column.orderEvents(selected, order);
  }

  /**
   * Get columns after applying a filter
   *
   * @returns {Array}
   * @private
   */
  function _getFiltered() {
    return $filter('filter')(vm.columns, {
      $: vm.searchTerm,
    });
  }

  /**
   * Expands an abbreviated word from a predefined list
   *
   * @param {String} label
   * @returns {String}
   * @private
   */
  function _expandAbbreviatedLabel(label) {
    var abbreviatedWords = [
      { abbr: 'Avg.', label: 'Average' },
      { abbr: 'Rev.', label: 'Revenue' },
    ];

    return reduce(abbreviatedWords, function(newLabel, word) {
      if (newLabel.includes(word.abbr)) {
        return newLabel.replace(word.abbr, word.label);
      }

      return newLabel;
    }, label);
  }

  /**
   * Apply selected columns on the table
   */
  function apply() {
    if (!vm.selectedColumns.length) {
      return;
    }

    Column.setSelected(vm.selectedColumns);

    vm.onClose();
  }

  /**
   * Checks if all items in view are selected
   *
   * @private
   */
  function _checkAllSelected() {
    vm.allSelected = Column.isAllSelected(_getFiltered());
  }

  /**
   * Toggle all filtered checkboxes
   *
   * @param {Boolean} state state to go to
   * @private
   */
  function _toggleFiltered(state) {
    let filteredList = _getFiltered();

    vm.allSelected = state;

    each(filteredList, function(column) {
      column.selected = state;
    });

    vm.selectedColumns = _filterSelected();
  }

  /**
   * Select all columns to be visible
   */
  function selectAll() {
    if (vm.allSelected) {
      return;
    }

    _toggleFiltered(true);

    clearSearch();
  }

  /**
   * Deselect all columns
   */
  function deselectAll() {
    if (!vm.selectedColumns.length) {
      return;
    }

    _toggleFiltered(false);
  }

  /**
   * Toggle event and corresponding revenue if given
   *
   * @param {Object} column columns that has been toggled
   * @private
   */
  function _toggleColumn(column, state) {
    var revColumn;

    column.selected = state;

    if (column.isCustom && endsWith(column.token, '_events')) {
      revColumn = find(vm.columns, { token: column.common + '_revenue' });
    }

    if (revColumn) {
      revColumn.selected = state;
    }

    _checkAllSelected();
  }

  /**
   * Search predicate for the column by token
   *
   * @param {Object} c
   * @param {String} token
   * @returns {Boolean}
   * @private
   */
  function _columnPredicate(c, token) {
    return c.common === token || c.token === token;
  }

  /**
   * Find column per specific token
   *
   * @param {String} token
   * @private
   */
  function _findColumn(token) {
    return find(vm.columns, c => _columnPredicate(c, token));
  }

  /**
   * Select targeted column
   *
   * @param {String} token
   * @param {String} targetToken
   * @param {Boolean} after
   */
  function select({ token, targetToken, after }) {
    let column = _findColumn(token);

    _toggleColumn(column, true);

    if (targetToken) {
      let positionIndex = vm.selectedColumns.findIndex(c => _columnPredicate(c, targetToken));
      vm.selectedColumns.splice(after ? positionIndex + 1 : positionIndex, 0, angular.copy(column));
    } else {
      vm.selectedColumns.push(angular.copy(column));
    }
  }

  /**
   * Deselelect targeted column
   *
   * @param {String} token
   */
  function deselect({ token }) {
    let column = _findColumn(token);

    _toggleColumn(column, false);

    remove(vm.selectedColumns, c => _columnPredicate(c, token));
  }

  /**
   * Clear search field
   *
   * @private
   */
  function clearSearch() {
    vm.searchTerm = '';
    vm.isSearchVisible = false;

    _checkAllSelected();
  }

  /**
   * Activate search visibility
   */
  function activateSearch() {
    vm.isSearchVisible = true;
  }

  /**
   * Set the new order
   *
   * @param {Array} order
   */
  function updateOrder({ order }) {
    vm.selectedColumns = _filterSelected(order);
  }

  /**
   * Prevent filter form submit from search input
   *
   * @param {Object} e
   */
  function preventSubmit(e) {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  }

  /**
   * Clear temporary selected columns
   *
   * @private
   */
  function _handleDestroy() {
    Column.destroySelected();
  }
}

export { ColumnSelectionController, columnSelection };
